import { createSelector } from "@reduxjs/toolkit";
import { State } from "redux/store";
import {
  CVATProject,
  DataSources,
  DatasetStatusEnum,
  FilesUploadSize,
  LastUsedData,
  RawCVATProject,
  RawCVATTask,
  StepTileProgressStatus,
  // New Dataset Creation Workflow Types
  FolderUploadStatus,
  UploadingFolder,
  MultiFolderUploadState,
  FolderMetadata,
  PreviewItem,
  // Dataset Processing
  CreationStatusResponse,
  FolderStatus,
} from "types";
import { Event } from "extractedBackendTypes";
import { getUserConfig } from "redux/User/selectors";
import { isFeatureEnabled } from "helpers/utility/utilities";

const EVENT_TYPES_TO_EXCLUDE: string[] = [
  "S3ValidURL",
  "S3FileDownloaded",
  "S3MediaPreview",
  "S3NoPreview",
  "FileMediaPreview",
  "FileNoPreview",
  "DataSourceEvent",
  "FilesChunkUploaded",
  "AnnotationsCleared",
  "FilesUploadCompleted",
  "LimitNotExceeded",
  "FilesUploadStart",
];
const state = (state: State) => state.createDataset;

export const getConnectStatus = createSelector(state, (state) => {
  return state.connectStatus;
});

export const getAnnotationStatus = createSelector(state, (state) => {
  return state.annotationStatus;
});

export const getUploadStatus = createSelector(
  state,
  getAnnotationStatus,
  (state, annotationStatus): StepTileProgressStatus => {
    if (
      annotationStatus === StepTileProgressStatus.COMPLETE ||
      state.uploadStatus === StepTileProgressStatus.FAILED
    ) {
      return state.uploadStatus;
    } else {
      return StepTileProgressStatus.DISABLED;
    }
  }
);

export const getIndexingStatus = createSelector(state, (state) => {
  return state.indexingStatus;
});

export const getS3FetchProgress = createSelector(state, (state) => {
  return state.s3FetchProgress;
});

export const getPreviewData = createSelector(state, (state) => {
  return state.previewData;
});

export const getPreviewDataSource = createSelector(
  state,
  (state): DataSources | null => {
    return state.previewDataSource;
  }
);

export const getNewDataset = createSelector(state, (state) => {
  return state.newDataset;
});

export const getNewDatasetId = createSelector(getNewDataset, (newDataset) => {
  return newDataset?.id;
});

export const getNewDatasetName = createSelector(getNewDataset, (newDataset) => {
  return newDataset ? newDataset.display_name : null;
});

export const getActivityLogs = createSelector(state, (state) => {
  return state.activityLogs;
});

// We filter out logs we don't want to display
export const getFilteredActivityLogs = createSelector(
  getActivityLogs,
  (activityLogs) => {
    return activityLogs.filter(
      (log: Event) => !EVENT_TYPES_TO_EXCLUDE.includes(log.event_type)
    );
  }
);

export const getimageErrorFileName = createSelector(state, (state) => {
  return state.imageErrorFileName;
});

export const getActivityOffset = createSelector(state, (state) => {
  return state.activityOffset;
});

export const getIsFatalError = createSelector(state, (state) => {
  return state.isFatalError;
});

export const getFilesUploadTransId = createSelector(state, (state) => {
  return state.filesUploadTransId;
});

export const getLimitExceededDetails = createSelector(state, (state) => {
  return state.limitExceededDetails;
});

export const getIsDatasetIndexing = createSelector(state, (state) => {
  return state.indexingStatus === StepTileProgressStatus.IN_PROGRESS;
});

export const getAreAnnotationsFetched = createSelector(state, (state) => {
  return state.areAnnotationsFetched;
});

export const getStatusCallInProgress = createSelector(state, (state) => {
  return state.statusCallInProgress;
});
export const getIsLoading = createSelector(state, (state) => {
  return state.isLoading;
});

export const getShowDeleteDatasetModal = createSelector(state, (state) => {
  return state.showDeleteDatasetModal;
});

export const getNewDatasetStatus = createSelector(state, (state) => {
  return state.datasetStatus;
});

export const getIsUploadInProgress = createSelector(state, (state) => {
  return state.uploadStatus === StepTileProgressStatus.IN_PROGRESS;
});

export const getIsNewDatasetInitializing = createSelector(
  getNewDataset,
  (newDataset) => {
    if (newDataset) {
      return newDataset.status === DatasetStatusEnum.INITIALIZING;
    } else {
      return false;
    }
  }
);

export const getLastUsedData = createSelector(state, (state): LastUsedData => {
  return state.lastUsedData;
});

export const getUploadFInished = createSelector(state, (state): boolean => {
  return state.uploadFinished;
});

export const getUserInitiatedCreation = createSelector(
  state,
  (state): boolean => {
    return state.userInitiatedCreation;
  }
);

export const getFilesUploadSize = createSelector(
  state,
  (state): FilesUploadSize => {
    return state.filesUploadSize;
  }
);

export const getFilesUploadProgress = createSelector(state, (state): number => {
  return state.filesUploadProgress;
});

export const getShowActivityLogModal = createSelector(state, (state) => {
  return state.showActivityLogModal;
});

export const getIsNewDatasetReady = createSelector(
  getNewDataset,
  (newDataset) => {
    if (newDataset) {
      return [DatasetStatusEnum.READY, DatasetStatusEnum.ENRICHING].includes(
        newDataset.status
      );
    } else {
      return false;
    }
  }
);

export const getRawCVATData = createSelector(
  state,
  (state): { tasks: RawCVATTask[]; projects: RawCVATProject[] } => {
    return state.cvatData;
  }
);

const formatCVATData = (data: {
  tasks: RawCVATTask[];
  projects: RawCVATProject[];
}): CVATProject[] => {
  return data.projects.map((project) => {
    const projectTasks = data.tasks.filter(
      (task) => task.project_id === project.id
    );

    return {
      id: project.id,
      name: project.name,
      tasks: projectTasks.map((task) => {
        return { id: task.id, name: task.name };
      }),
    };
  });
};

export const getFormattedCVATData = createSelector(
  getRawCVATData,
  (cvatData): CVATProject[] => {
    return formatCVATData(cvatData);
  }
);

export const getIsNewCreationEnabled = createSelector(
  getUserConfig,
  (userConfig) => {
    return isFeatureEnabled(userConfig, "DATASET_CREATION_V2");
  }
);

// New Dataset Creation Workflow Selectors
export const getFolderUploadStatus = createSelector(
  state,
  (state): FolderUploadStatus => state.folderUploadStatus
);

export const getUploadingFolder = createSelector(
  state,
  (state): UploadingFolder | null => state.uploadingFolder
);

export const getMultiFolderUpload = createSelector(
  state,
  (state): MultiFolderUploadState | null => state.multiFolderUpload
);

export const getFolderMetadata = createSelector(
  state,
  (state): FolderMetadata[] => state.foldersMetadata
);

export const getInvalidFolders = createSelector(
  state,
  (state): FolderMetadata[] => state.invalidFolders
);

export const getValidationLoading = createSelector(
  state,
  (state): boolean => state.validationLoading
);

export const getPreviews = createSelector(
  state,
  (state): PreviewItem[] => state.previews
);

export const getPreviewsLoading = createSelector(
  state,
  (state): boolean => state.previewsLoading
);

export const getFolderMetadataPolling = createSelector(
  state,
  (state): boolean => state.folderMetadataPolling
);

export const getLocalFolderSelections = createSelector(
  state,
  (state): string[] => state.localFolderSelections
);

export const getFolderMetadataStatus = createSelector(
  getFolderMetadata,
  (folders): FolderStatus | undefined => {
    if (!folders || folders.length === 0) return undefined;
    return folders.every((f) => f.status === "valid") ? "valid" : undefined;
  }
);

// Combined selector: server metadata + local selections
export const getFoldersWithLocalSelections = createSelector(
  [getFolderMetadata, getLocalFolderSelections],
  (folders, localSelections): FolderMetadata[] => {
    const selectionSet = new Set(localSelections);
    return folders.map((folder) => ({
      ...folder,
      selected: selectionSet.has(folder.id),
    }));
  }
);

export const getCreationStatus = createSelector(
  state,
  (state): CreationStatusResponse | null => state.creationStatus
);

export const getCreationStatusPolling = createSelector(
  state,
  (state): boolean => state.creationStatusPolling
);

// Check if selected folders are ready (backend status is 'ready')
export const getSelectedFoldersReadyStatus = createSelector(
  [getFoldersWithLocalSelections],
  (folders): { allReady: boolean; readyFolderIds: string[] } => {
    const selectedFolders = folders.filter((f) => f.selected);
    const readyFolders = selectedFolders.filter(
      (f) => f.backend_status === "ready"
    );

    return {
      allReady:
        selectedFolders.length > 0 &&
        selectedFolders.length === readyFolders.length,
      readyFolderIds: readyFolders.map((f) => f.id),
    };
  }
);
